import { useState, type ReactElement } from 'react';
import escochexLogo from '../../../assets/escochex-logo.webp';
import '../admin-signup.css';
import { ThemeToggle } from '../../../shared/theme/ThemeToggle';
import { PRIMARY_STEPS, WIZARD_STEPS, primaryStepFor, type WizardStepKey } from '../types';
import { CompanyInfoStep } from './steps/CompanyInfoStep';
import { IdentityVerificationStep } from './steps/IdentityVerificationStep';
import { AddressStep } from './steps/AddressStep';
import { BankingStep } from './steps/BankingStep';
import { AdminInfoStep } from './steps/AdminInfoStep';
import { WelcomeStep } from './steps/WelcomeStep';

export interface StepProps {
  goNext: () => void;
  goBack: () => void;
}

const STEP_COMPONENTS: Record<WizardStepKey, (props: StepProps) => ReactElement> = {
  'company-info': CompanyInfoStep,
  'identity-verification': IdentityVerificationStep,
  address: AddressStep,
  banking: BankingStep,
  'admin-info': AdminInfoStep,
  welcome: WelcomeStep,
};

/**
 * Admin Sign Up — the approved 4-step onboarding flow (Company Information + Identity Verification,
 * Company Address, Banking Details and Payroll Setup, Admin Information), plus the Welcome/Success screen.
 * Mirrors the approved FINAL HTML's own two-column `.setup-shell` layout: a persistent left-panel numbered
 * stepper (exactly 4 primary steps — Identity Verification is presented as a sub-step of Company
 * Information via the `.substepper`, never a 5th numbered step) alongside the active screen.
 * Step position is held in local component state, not the URL: unlike CustomPayRunWizard's `:runId/:step`
 * route (an authenticated user resuming a specific, already-owned run), Admin Sign Up's only durable state
 * is the server-side CompanyRegistration behind the httpOnly onboarding cookie the browser already holds —
 * putting a registration id in a public URL would add nothing but an unnecessary, guessable identifier.
 */
export function AdminSignUpPage() {
  const [step, setStep] = useState<WizardStepKey>('company-info');

  const stepIndex = WIZARD_STEPS.findIndex((s) => s.key === step);
  const goNext = () => {
    const next = WIZARD_STEPS[stepIndex + 1];
    if (next) setStep(next.key);
  };
  const goBack = () => {
    const previous = WIZARD_STEPS[stepIndex - 1];
    if (previous) setStep(previous.key);
  };

  const StepComponent = STEP_COMPONENTS[step];
  const primaryStep = primaryStepFor(step);
  const primaryIndex = PRIMARY_STEPS.findIndex((s) => s.key === primaryStep);
  const isWelcome = step === 'welcome';

  return (
    <div className="admin-signup-shell" data-testid="admin-signup-page">
      <ThemeToggle />
      <section className="setup-shell">
        <aside className="left-panel">
          <div className="brand">
            <img src={escochexLogo} alt="EscoChex — A Payroll Software Company" />
          </div>
          <h1 className="setup-title">Business Profile Setup</h1>
          <div className="main-steps" aria-label="Sign up steps">
            {PRIMARY_STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`main-step${i === primaryIndex && !isWelcome ? ' active' : ''}${i < primaryIndex || isWelcome ? ' done' : ''}`}
                aria-current={i === primaryIndex && !isWelcome ? 'step' : undefined}
              >
                <div className="circle">
                  <span>{i + 1}</span>
                </div>
                <div className="copy">
                  <strong>{s.label}</strong>
                  <span>{s.description}</span>
                </div>
              </div>
            ))}
          </div>
        </aside>

        <section className="form-panel">
          <div className="form-panel-inner">
            <StepComponent goNext={goNext} goBack={goBack} />
          </div>
        </section>
      </section>
    </div>
  );
}
